import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  alpha,
  useTheme,
  Tooltip,
} from "@mui/material";
import { Plus, Trash2, Edit2, Users } from "lucide-react";
import {
  fetchMarketingSegments,
  createMarketingSegment,
  updateMarketingSegment,
  deleteMarketingSegment,
} from "../../../../../../redux/actions/marketingAction";
import DeleteConfirmDialog from "../DeleteConfirmDialog";

const PRESET_COLORS = ["#3B82F6", "#10b981", "#f59e0b", "#ec4899", "#8b5cf6", "#06b6d4", "#f97316", "#84cc16"];

const SegmentList = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { segments } = useSelector((state) => state.marketing);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingSegment, setEditingSegment] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "", color: "#3B82F6" });

  useEffect(() => {
    dispatch(fetchMarketingSegments());
  }, [dispatch]);

  const handleOpen = (segment = null) => {
    setEditingSegment(segment);
    setFormData({
      name: segment?.name || "",
      description: segment?.description || "",
      color: segment?.color || "#3B82F6",
    });
    setFormOpen(true);
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSave = async () => {
    setSubmitting(true);
    try {
      if (editingSegment) {
        await dispatch(updateMarketingSegment(editingSegment.id, formData));
      } else {
        await dispatch(createMarketingSegment(formData));
      }
      setFormOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const items = segments || [];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
          {t('mkt_segments')} ({items.length})
        </Typography>
        <Button variant="contained" size="small" startIcon={<Plus size={16} />} onClick={() => handleOpen()} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}>
          {t('mkt_new_segment')}
        </Button>
      </Box>

      {items.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, textAlign: "center" }}>
          <Typography sx={{ color: theme.palette.text.secondary }}>{t('mkt_no_segments')}</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {items.map((segment) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={segment.id}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: "12px",
                  border: `1px solid ${theme.palette.divider}`,
                  borderLeft: `4px solid ${segment.color || theme.palette.primary.main}`,
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: alpha(segment.color || theme.palette.primary.main, 0.3),
                  },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{segment.name}</Typography>
                {segment.description && (
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block", mb: 1 }}>
                    {segment.description}
                  </Typography>
                )}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.5 }}>
                  <Users size={14} color={theme.palette.text.secondary} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {(segment.contact_count || 0).toLocaleString()} {t('mkt_contacts_unit')}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleOpen(segment)}><Edit2 size={14} /></IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => setDeleteTarget(segment)}>
                      <Trash2 size={14} color={theme.palette.error?.main} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{editingSegment ? t('mkt_edit_segment') : t('mkt_new_segment')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField label={t('common_name')} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} fullWidth size="small" required />
            <TextField label={t('mkt_description')} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} fullWidth size="small" multiline rows={2} />
            <Box>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mb: 1, display: "block" }}>{t('mkt_color')}</Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {PRESET_COLORS.map((c) => (
                  <Box
                    key={c}
                    onClick={() => setFormData({ ...formData, color: c })}
                    sx={{
                      width: 28, height: 28, borderRadius: "6px", bgcolor: c, cursor: "pointer",
                      border: formData.color === c ? "2px solid white" : "none",
                      boxShadow: formData.color === c ? `0 0 0 2px ${c}` : "none",
                      transition: "all 0.2s",
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFormOpen(false)}>{t('common_cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={!formData.name || submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}>
            {submitting ? t('mkt_saving') : t('common_save')}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          await dispatch(deleteMarketingSegment(deleteTarget.id));
          setDeleteTarget(null);
        }}
        itemName={deleteTarget?.name}
        itemType={t("mkt_segment")}
      />
    </Box>
  );
};

export default SegmentList;
